// contexts/auth-provider.tsx
"use client"

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { api } from '@edemics/lib/api'

/**
 * Router-agnostic navigation. Consumers inject `navigate` (e.g. Next's
 * `router.push` or react-router's `navigate`). Falls back to a full-page
 * `window.location.assign` if not provided — usable but not ideal for SPAs.
 */
type NavigateFn = (path: string) => void

const defaultNavigate: NavigateFn = (path) => {
  if (typeof window !== 'undefined') window.location.assign(path)
}

/**
 * PF-2 — the JWT audience is the authoritative identity kind. It arrives
 * on every /auth/*-login response and on /auth/me as `kind`.
 *
 *   'tenant'   → tenant-scoped user (users table). tenantId is always set.
 *   'platform' → platform staff (platform_users table). tenantId is null.
 *
 * Consumers should discriminate on `kind` — never on `role`. The old
 * role-string sniffing (`role === 'admin' | 'super-admin'`) is gone
 * because tenant admins are a distinct concept from platform staff.
 */
export type UserKind = 'tenant' | 'platform'

interface User {
  id: string
  userId?: string
  email: string
  name: string
  role: string
  /** PF-2 authoritative identity discriminator (JWT audience). */
  kind: UserKind
  /**
   * Non-null for kind='tenant' (User.tenantId is NOT NULL post-PF-2).
   * Null for kind='platform' — the platform identity is cross-tenant by
   * construction. Union stays `string | null` so the discriminated shape
   * lives in one field pair without over-fitting the type.
   */
  tenantId: string | null
  /**
   * Module visibility (2026-08-26) — resolved keys enabled for this
   * tenant, delivered on the login response and /auth/me. Platform
   * users don't receive this — they bypass module gating.
   */
  enabledModules?: string[]
  /**
   * Module visibility explicit-off set (2026-08-27) — resolved catalog
   * keys the tenant currently has TURNED OFF (via TenantModuleOverride
   * or SAModule/SASubmodule.defaultEnabled=false). Unlike enabledModules
   * this is what the sidebar gates on: only explicit-off hides items,
   * so a submodule the seed hasn't caught up on still renders.
   */
  disabledModules?: string[]
  /**
   * H4 (2026-09-03) — every catalog key the tenant sees as
   * "premium-locked": the module is flagged isPremium=true, the
   * tenant's plan.tier isn't 'Premium', and no super-admin override
   * has comped access. `disabledModules` also lists these keys (they
   * count as explicit-off for the sidebar), but `<ModuleGate>` uses
   * this set to render the upsell page instead of the generic "not
   * available" one.
   */
  premiumModules?: string[]
  /**
   * Doctype visibility (2026-08-27) — same shape as enabledModules
   * but at doctype granularity ("Student", "JournalEntry", etc.). A
   * doctype missing from this list is disabled from the FE's
   * perspective. Applies to user-facing surface only — internal
   * cross-module references keep working regardless.
   */
  enabledDoctypes?: string[]
  /**
   * Multi-app-role (2026-08-27) — every active app-role kind this
   * user holds (admin, teacher, parent, student, alumni). `role`
   * above is the currently-active one; the switcher flips between
   * everything in this list. Legacy tokens / platform users read
   * undefined here.
   */
  appRoles?: string[]
  /**
   * Onboarding gate (2026-08-28) — mirrors tenant.School.onboardingCompleted
   * from the BE. AuthGuard sends tenant admins to /onboarding when
   * this is explicitly false. Missing / undefined is treated as "we
   * don't know yet" (pre-gate session-restore); the guard falls
   * through in that case and lets the user through. Platform users
   * bypass the guard entirely, so this stays undefined for them.
   */
  onboardingCompleted?: boolean
  /**
   * Force-password-change gate (2026-08-28) — mirrors
   * User.mustChangePasswordAt from the BE. Stamped by
   * /tenant-settings/force-password-change; cleared by
   * /auth/change-password. AuthGuard routes the user to
   * /change-password when explicitly true, ignoring every other
   * route request until they change their password.
   */
  mustChangePassword?: boolean
  /**
   * Branch scope (Q1 2026-09-04) — non-null when the user is scoped to
   * a specific branch (branch admin, branch-scoped teacher, etc.).
   * Loaded from the JWT strategy and echoed on every login response
   * plus /auth/me. Onboarding surfaces (students, staff) use it to
   * auto-fill + readonly the branch picker for branch admins so they
   * can't accidentally assign records to another branch.
   */
  branchId?: string | null
}

/**
 * Optional prop signature: apps that own their own post-login destination
 * (e.g. super-admin lands on `/dashboard`, not `/dashboard/${role}`) can
 * override the default role-scoped redirect. Return value MUST be a path.
 */
type PostLoginPathFn = (user: User) => string

interface AuthContextType {
  user: User | null
  login: (email: string, password: string) => Promise<User>
  logout: () => void
  /**
   * Multi-app-role (2026-08-27) — POST /auth/switch-role. Validates the
   * target is in `user.appRoles`, mints a fresh JWT, swaps token +
   * user state, navigates to /dashboard. Resolves to the updated User.
   * Rejects with an Error whose .message the caller can surface.
   */
  switchRole: (role: string) => Promise<User>
  /**
   * Force /auth/me refetch (2026-08-28). Server-side flags that mutate
   * during a session — onboardingCompleted, mustChangePassword,
   * enabledModules — get out of sync with the stored user object. Call
   * this after any flow that flips one of those flags (e.g. finishing
   * onboarding, changing password). Resolves to the fresh user; rejects
   * on 401 (auth-provider clears storage in that case).
   */
  refreshUser: () => Promise<User | null>
  isAuthenticated: boolean
  isLoading: boolean
  /**
   * PF-2 — derived from `user.kind === 'platform'` (JWT audience), never
   * from role strings. A tenant admin (`role === 'admin'`) is NOT a
   * platform owner and will read false here.
   */
  isSuperAdmin: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
  /** Router push fn — pass Next's `useRouter().push` or react-router's `navigate`. Falls back to `window.location.assign`. */
  navigate?: NavigateFn
  /**
   * Optional post-login redirect path resolver. Falls back to the legacy
   * `/dashboard/${role}` shape the tenant-app relies on. The super-admin
   * app passes `() => '/super-admin'` so it lands on its own root.
   */
  postLoginPath?: PostLoginPathFn
  /**
   * PF-2 — API path the login form POSTs to. Two audiences, two paths:
   *
   *   '/auth/tenant-login'   → tenant-app (default)
   *   '/auth/platform-login' → super-admin app
   *
   * The BE also serves '/auth/login' as a back-compat alias to the tenant
   * endpoint for the sunset window; leaving this prop unset preserves
   * that shape. Super-admin MUST set '/auth/platform-login' — the tenant
   * alias will never mint a platform-audience token.
   */
  loginEndpoint?: string
}

function computeIsSuperAdmin(user: User | null): boolean {
  return !!user && user.kind === 'platform'
}

/**
 * Drain a `#token=<jwt>&user=<base64-json>&role=<active-role>` URL
 * fragment into sessionStorage. Used by the cross-host handoff when
 * the tenant-app apex login discovers a platform user and redirects
 * them to admin.edemics.com — the target app needs the token to be in
 * sessionStorage BEFORE it reads it. Idempotent via history.replaceState.
 *
 * A near-identical copy lives in tenant-provider.tsx; both providers
 * call it, and the second call is a no-op because the fragment is
 * cleared on the first drain.
 *
 * ORDERING NOTE — main.tsx also calls this BEFORE ReactDOM.createRoot
 * because `<Navigate>` elements that redirect a landing route (e.g.
 * super-admin's `/dashboard → /super-admin` alias) commit their
 * history.replace() in a child useEffect that fires BEFORE
 * AuthProvider's mount effect (bottom-up effect order) — that
 * history.replace strips the fragment. Draining synchronously in
 * main.tsx avoids the race entirely. The AuthProvider call below is
 * kept as belt-and-suspenders for hosts that don't have the main.tsx
 * hook wired.
 */
export function hydrateSessionFromUrlFragment(): void {
  if (typeof window === 'undefined') return
  const rawHash = window.location.hash
  if (!rawHash || !rawHash.startsWith('#')) return
  const params = new URLSearchParams(rawHash.slice(1))
  const token = params.get('token')
  if (!token) return
  try {
    sessionStorage.setItem('token', token)
    const encodedUser = params.get('user')
    if (encodedUser) {
      try {
        const decoded = atob(encodedUser)
        const parsed = JSON.parse(decoded)
        sessionStorage.setItem('user', JSON.stringify(parsed))
        if (parsed?.role) sessionStorage.setItem('userRole', String(parsed.role))
      } catch {
        // Malformed user payload — leave the token so /auth/me can
        // rehydrate via the mount-effect refresh path below.
      }
    }
  } catch {
    // Storage blocked (private browsing quota, etc.) — the user will
    // just land on /login and re-authenticate. Not fatal.
    return
  }
  // Strip the fragment so a refresh / back-nav doesn't re-consume it
  // and so the token doesn't sit in the address bar.
  try {
    const cleanUrl = window.location.pathname + window.location.search
    window.history.replaceState(null, '', cleanUrl)
  } catch {
    // If replaceState is unavailable, the token is already in storage —
    // the visible fragment is cosmetic damage only.
  }
}

export function AuthProvider({
  children,
  navigate = defaultNavigate,
  postLoginPath,
  loginEndpoint = '/auth/tenant-login',
}: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Cross-host session handoff (2026-09-05). The tenant-app apex-login
    // flow redirects platform users to admin.edemics.com/dashboard with a
    // `#token=<jwt>&user=<b64>` fragment; the super-admin app has no
    // TenantProvider, so the fragment used to sit in the URL and the
    // AuthProvider read an empty sessionStorage → bounced to /login → the
    // "infinite redirect loop" symptom users reported. Draining here (as
    // opposed to only in TenantProvider) makes the handoff work for every
    // app that mounts AuthProvider, present and future.
    hydrateSessionFromUrlFragment()

    // One-time migration from the old localStorage-based session store
    // to sessionStorage (2026-09-01). Multi-tenant tab isolation — each
    // tab owns its own session so two tenant admins can be signed in
    // side-by-side. Existing users still on their old localStorage
    // session get pulled into the new home on their first visit after
    // the deploy, so we don't kick them out. localStorage copies stay
    // in place too so a tab reopened via history restore still finds a
    // session — this fallback is REMOVED on the next login so a stale
    // localStorage session can't leak into a fresh sign-in.
    try {
      if (!sessionStorage.getItem('token') && localStorage.getItem('token')) {
        sessionStorage.setItem('token', localStorage.getItem('token')!)
        const u = localStorage.getItem('user')
        if (u) sessionStorage.setItem('user', u)
        const r = localStorage.getItem('userRole')
        if (r) sessionStorage.setItem('userRole', r)
      }
    } catch { /* storage blocked — user re-signs in */ }

    // Check for existing token on mount
    const token = sessionStorage.getItem('token')
    const userData = sessionStorage.getItem('user')

    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData)

        // Heal any desync between the stored user object and the userRole key
        // (e.g. a portal switch happened before this fix was in place).
        const storedRole = sessionStorage.getItem('userRole')
        if (storedRole && parsedUser.role !== storedRole) {
          parsedUser.role = storedRole
          sessionStorage.setItem('user', JSON.stringify(parsedUser))
        }

        // PF-2 — heal a pre-PF-2 stored user object that has no `kind`.
        // Infer from tenantId (null → platform, non-null → tenant); this
        // matches how the BE looked up the identity pre-PF-2 (super-admin
        // was the only null-tenantId path). Rewritten to storage so the
        // next reload doesn't re-infer.
        if (!parsedUser.kind) {
          parsedUser.kind = parsedUser.tenantId ? 'tenant' : 'platform'
          sessionStorage.setItem('user', JSON.stringify(parsedUser))
        }

        setUser(parsedUser)
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`

        // Background /auth/me refresh (2026-08-28). Stored user objects
        // are cached indefinitely — server-side flags like
        // onboardingCompleted / mustChangePassword / enabledModules
        // may have flipped since the last login and the guards
        // downstream will otherwise gate against stale values (bug
        // Ayodeji hit: finished onboarding but stuck in redirect loop
        // because localStorage still said onboardingCompleted=false).
        // Fire-and-forget; UI already has an optimistic user object.
        // 401 → session invalidated (BE just booted us) → clear
        // storage so the app treats us as logged out.
        api
          .get('/auth/me')
          .then((res) => {
            const fresh = res.data
            const normalized: User = {
              ...fresh,
              id: fresh.id ?? fresh.userId,
              userId: fresh.userId ?? fresh.id,
              tenantId: fresh.tenantId ?? null,
              kind:
                fresh.kind ?? (fresh.tenantId ? 'tenant' : 'platform'),
            }
            sessionStorage.setItem('user', JSON.stringify(normalized))
            setUser(normalized)
          })
          .catch((err) => {
            if (err?.response?.status === 401) {
              sessionStorage.removeItem('token')
              sessionStorage.removeItem('user')
              sessionStorage.removeItem('userRole')
              setUser(null)
              delete api.defaults.headers.common['Authorization']
            }
            // Non-401 (network hiccup, 5xx, etc.) — keep the cached
            // user; downstream guards default-safe to "let through"
            // when the flags are missing.
          })
      } catch (e) {
        // Corrupted storage — clear it so auth restore can't get stuck.
        sessionStorage.removeItem('token')
        sessionStorage.removeItem('user')
        sessionStorage.removeItem('userRole')
      }
    }
    setIsLoading(false)
  }, [])

  /**
   * Refetch /auth/me and update stored user in place. See the docstring
   * on AuthContextType.refreshUser for when to call this.
   */
  const refreshUser = async (): Promise<User | null> => {
    try {
      const res = await api.get('/auth/me')
      const fresh = res.data
      const normalized: User = {
        ...fresh,
        id: fresh.id ?? fresh.userId,
        userId: fresh.userId ?? fresh.id,
        tenantId: fresh.tenantId ?? null,
        kind: fresh.kind ?? (fresh.tenantId ? 'tenant' : 'platform'),
      }
      sessionStorage.setItem('user', JSON.stringify(normalized))
      setUser(normalized)
      return normalized
    } catch (err: any) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem('token')
        sessionStorage.removeItem('user')
        sessionStorage.removeItem('userRole')
        setUser(null)
        delete api.defaults.headers.common['Authorization']
      }
      throw err
    }
  }

  const login = async (email: string, password: string): Promise<User> => {
    try {
      const response = await api.post(loginEndpoint, { email, password })
      const { access_token, user: userData } = response.data

      // Ensure both id and userId are present, and normalise the PF-2
      // discriminator. The BE always sends `kind` on the new endpoints,
      // but if a caller ever wires this against a legacy alias, fall
      // back on the tenantId shape (non-null → tenant, null → platform).
      const inferredKind: UserKind =
        userData.kind ?? (userData.tenantId ? 'tenant' : 'platform')
      const normalizedUser: User = {
        ...userData,
        userId: userData.userId || userData.id,
        id: userData.id || userData.userId,
        kind: inferredKind,
        // Null tenantId is legitimate iff kind === 'platform'.
        tenantId: userData.tenantId ?? null,
        // Module visibility — tenant users get the resolved set from the
        // BE. Platform users have no per-tenant gate so the array stays
        // undefined; the ModuleContext treats that as "no gating".
        enabledModules: Array.isArray(userData.enabledModules)
          ? userData.enabledModules
          : undefined,
        disabledModules: Array.isArray(userData.disabledModules)
          ? userData.disabledModules
          : undefined,
        // H4 (2026-09-03) — premium-gated keys. Missing / non-array
        // shape means either legacy BE (pre-H4) or platform user;
        // treated as "no premium gate" by consumers.
        premiumModules: Array.isArray(userData.premiumModules)
          ? userData.premiumModules
          : undefined,
        onboardingCompleted:
          typeof userData.onboardingCompleted === "boolean"
            ? userData.onboardingCompleted
            : undefined,
        mustChangePassword:
          typeof userData.mustChangePassword === "boolean"
            ? userData.mustChangePassword
            : undefined,
        // Branch scope (Q1 2026-09-04) — non-null for branch-scoped users.
        // Null / missing means the user is unscoped (HQ admin, platform).
        branchId:
          typeof userData.branchId === "string"
            ? userData.branchId
            : null,
        // Doctype visibility — same normalization + null semantics
        // as enabledModules. Missing / non-array shape means "no
        // doctype gate" from the FE's perspective.
        enabledDoctypes: Array.isArray(userData.enabledDoctypes)
          ? userData.enabledDoctypes
          : undefined,
        // Multi-app-role — every active app-role kind. Legacy login
        // responses (pre-multi-role) omit this; the switcher hides
        // itself in that case.
        appRoles: Array.isArray(userData.appRoles)
          ? userData.appRoles
          : undefined,
      }

      sessionStorage.setItem('token', access_token)
      sessionStorage.setItem('user', JSON.stringify(normalizedUser))
      sessionStorage.setItem('userRole', normalizedUser.role)
      // Purge legacy localStorage session so a fresh sign-in never
      // inherits a stale identity if two tenants share the same
      // browser (2026-09-01 multi-tenant isolation).
      try {
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        localStorage.removeItem('userRole')
      } catch { /* ignore */ }

      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`

      setUser(normalizedUser)

      // Redirect: consumer-provided path (super-admin + tenant-app both
      // pass one) wins. Fallback is `/dashboard` — single path; sidebar
      // reads role from user context to render the right menu. The legacy
      // `/dashboard/${role}` trampoline is gone as of tenant-app Phase 1
      // (2026-08-26); no route resolves that shape.
      const target = postLoginPath?.(normalizedUser) ?? '/dashboard'
      // P1 (V5 pg21) — first-login race fix. React 18's automatic
      // batching normally lets `setUser` + `navigate` land in one render,
      // but with react-router-dom the navigation dispatch can fire
      // BEFORE the setUser update finishes propagating to context
      // consumers, so AuthGuard on the destination reads
      // isAuthenticated=false and bounces back to /login (the "sign in
      // twice" bug users saw on cold start). Deferring the navigate to
      // the next microtask via `setTimeout(_, 0)` guarantees the
      // setUser render has committed and every downstream guard sees
      // the fresh user object.
      if (typeof window !== 'undefined') {
        window.setTimeout(() => navigate(target), 0)
      } else {
        navigate(target)
      }

      return normalizedUser
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Login failed')
    }
  }

  /**
   * Multi-app-role (2026-08-27) — POST /auth/switch-role. The BE
   * validates the target is in the user's active appRoles, mints a
   * fresh JWT with the new active role, and returns the same shape as
   * tenant-login. We drop the new token + user object into storage,
   * update state, and navigate to `/dashboard`.
   *
   * If the caller passes a role NOT in `user.appRoles`, the BE returns
   * 400 — we surface the error rather than optimistically swapping
   * local state.
   */
  const switchRole = async (role: string): Promise<User> => {
    if (!user) {
      throw new Error('Not authenticated')
    }
    if (user.kind === 'platform') {
      throw new Error('Platform users cannot switch app roles')
    }
    if (user.role === role) {
      // No-op — already on this role. Just navigate to dashboard so the
      // click always feels responsive.
      navigate('/dashboard')
      return user
    }

    try {
      const response = await api.post('/auth/switch-role', { role })
      const { access_token, user: userData } = response.data

      const inferredKind: UserKind =
        userData.kind ?? (userData.tenantId ? 'tenant' : 'platform')
      const normalizedUser: User = {
        ...userData,
        userId: userData.userId || userData.id,
        id: userData.id || userData.userId,
        kind: inferredKind,
        tenantId: userData.tenantId ?? null,
        enabledModules: Array.isArray(userData.enabledModules)
          ? userData.enabledModules
          : undefined,
        disabledModules: Array.isArray(userData.disabledModules)
          ? userData.disabledModules
          : undefined,
        // H4 (2026-09-03) — premium-gated keys. Missing / non-array
        // shape means either legacy BE (pre-H4) or platform user;
        // treated as "no premium gate" by consumers.
        premiumModules: Array.isArray(userData.premiumModules)
          ? userData.premiumModules
          : undefined,
        onboardingCompleted:
          typeof userData.onboardingCompleted === "boolean"
            ? userData.onboardingCompleted
            : undefined,
        mustChangePassword:
          typeof userData.mustChangePassword === "boolean"
            ? userData.mustChangePassword
            : undefined,
        // Branch scope (Q1 2026-09-04) — mirror in switch-role response so
        // switching between teacher/parent/admin roles keeps the branch
        // context intact for the branch-admin onboarding UX.
        branchId:
          typeof userData.branchId === "string"
            ? userData.branchId
            : null,
        appRoles: Array.isArray(userData.appRoles)
          ? userData.appRoles
          : undefined,
      }

      sessionStorage.setItem('token', access_token)
      sessionStorage.setItem('user', JSON.stringify(normalizedUser))
      sessionStorage.setItem('userRole', normalizedUser.role)
      // Purge legacy localStorage session so a fresh sign-in never
      // inherits a stale identity if two tenants share the same
      // browser (2026-09-01 multi-tenant isolation).
      try {
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        localStorage.removeItem('userRole')
      } catch { /* ignore */ }
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`

      setUser(normalizedUser)
      navigate('/dashboard')
      return normalizedUser
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to switch role')
    }
  }

  const logout = () => {
    sessionStorage.removeItem('token')
    sessionStorage.removeItem('user')
    sessionStorage.removeItem('userRole')
    delete api.defaults.headers.common['Authorization']
    setUser(null)
    navigate('/login')
  }

  return (
    <AuthContext.Provider value={{
      user,
      login,
      logout,
      switchRole,
      refreshUser,
      isAuthenticated: !!user,
      isLoading,
      isSuperAdmin: computeIsSuperAdmin(user),
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
